/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { isImageLinkQueued } from '@theme/utilities';

/** Touch devices without hover — thumbnails are always visible; see product-variations.liquid */
const mediaQueryTouch = matchMedia('(hover: none) and (pointer: coarse)');

/**
 * Wires up hover-preview + tap-to-select interaction for a set of variation
 * thumbnails (each marked `data-variation-hover-init`) inside a product card:
 * hovering/focusing swaps the card's main image and price; on desktop a
 * click navigates natively, on touch the first tap selects and the second
 * follows the link. Shared by `card_hover`'s `<card-variation-hover>` and by
 * `buttons` mode's inline swatches (assets/product-variations.js) when
 * rendered inside a product-card.
 *
 * @param {HTMLElement} container - Element whose descendants carry `data-variation-hover-*`.
 * @param {HTMLElement} productCard - The ancestor `<product-card>` / `.resource-card`.
 * @returns {() => void} Cleanup function that removes every listener this created.
 */
export function bindCardVariationHover(container, productCard) {
  /** @type {HTMLImageElement | null} */
  const mainImage = productCard.querySelector(
    'slideshow-slide img, .card-gallery img, .media img, img.resource-card__image, img[src*="products"]',
  );
  const priceElement = productCard.querySelector('product-price [ref="priceContainer"] .price');
  const productLink = /** @type {HTMLAnchorElement | null} */ (productCard.querySelector('[ref="productCardLink"]'));
  const galleryLink = /** @type {HTMLAnchorElement | null} */ (productCard.querySelector('[ref="cardGalleryLink"]'));
  const titleLink = /** @type {HTMLAnchorElement | null} */ (productCard.querySelector('[ref="productTitleLink"]'));
  const primaryVariation = /** @type {HTMLElement | null} */ (
    container.querySelector('[data-variation-hover-primary]')
  );

  /** @type {HTMLElement | null} Currently selected/previewed thumbnail; falls back to primaryVariation. */
  let selectedVariation = primaryVariation;

  const mainImageSizes = mainImage?.sizes?.replace(/^\s*auto\s*,\s*/i, '');

  // The thumbnails are real anchors that navigate to each variation's PDP, so
  // they're exposed as a labelled group of links (not a listbox of options).
  // The hover/focus image swap is a visual-only enhancement for sighted users.
  container.setAttribute('role', 'group');
  container.setAttribute('aria-label', 'Product color variations');

  /** @param {HTMLElement} element */
  function applyVariation(element) {
    const { variationHoverImage, variationHoverSrcset, variationHoverPrice, variationHoverUrl } = element.dataset;

    if (mainImage && variationHoverImage) {
      mainImage.src = variationHoverImage;
      mainImage.srcset = variationHoverSrcset || '';
    }

    if (priceElement && variationHoverPrice) {
      priceElement.textContent = variationHoverPrice;
    }

    if (variationHoverUrl) {
      if (productLink) productLink.href = variationHoverUrl;
      if (galleryLink) galleryLink.href = variationHoverUrl;
      if (titleLink) titleLink.href = variationHoverUrl;
    }

    // Visual-only highlight of the previewed thumbnail; no ARIA selection
    // state since these are links, not options in a listbox.
    for (const thumb of container.querySelectorAll('[data-variation-hover-init]')) {
      thumb.classList.toggle('is-active', thumb === element);
    }
  }

  /** @param {HTMLElement} selected */
  function updateAriaCurrent(selected) {
    for (const thumb of container.querySelectorAll('[data-variation-hover-init]')) {
      if (thumb === selected) {
        thumb.setAttribute('aria-current', 'page');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /** @param {MouseEvent} event */
  function handleClick(event) {
    if (!mediaQueryTouch.matches) return;

    const target = event.target instanceof Element ? event.target : null;
    const thumbnail = /** @type {HTMLElement | null} */ (target?.closest('[data-variation-hover-init]'));
    if (!thumbnail || thumbnail === selectedVariation) return;

    event.preventDefault();
    selectedVariation = thumbnail;
    applyVariation(thumbnail);
    updateAriaCurrent(thumbnail);
  }

  /** @param {MouseEvent | FocusEvent} event */
  function handleInteraction(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const thumbnail = /** @type {HTMLElement | null} */ (target?.closest('[data-variation-hover-init]'));
    if (thumbnail) applyVariation(thumbnail);
  }

  function handleLeave() {
    const fallback = selectedVariation || primaryVariation;
    if (fallback) applyVariation(fallback);
  }

  /** @param {FocusEvent} event */
  function handleFocusOut(event) {
    const relatedTarget = /** @type {HTMLElement | null} */ (event.relatedTarget);
    if (!productCard.contains(relatedTarget)) {
      const fallback = selectedVariation || primaryVariation;
      if (fallback) applyVariation(fallback);
    }
  }

  const listenerController = new AbortController();
  const { signal } = listenerController;

  container.addEventListener('mouseover', handleInteraction, { signal });
  container.addEventListener('focusin', handleInteraction, { signal });
  container.addEventListener('click', handleClick, { signal });
  productCard.addEventListener('mouseleave', handleLeave, { signal });
  productCard.addEventListener('focusout', handleFocusOut, { signal });

  // Prefetch every non-active variation's image once the pointer enters the
  // card, so a later hover/tap over a thumbnail paints instantly.
  const nonActiveThumbnails = /** @type {HTMLElement[]} */ (
    Array.from(container.querySelectorAll('[data-variation-hover-srcset]')).filter(
      (t) => t instanceof HTMLElement && !t.classList.contains('is-active'),
    )
  );
  const prefetchController = new AbortController();
  if (nonActiveThumbnails.length) {
    productCard.addEventListener(
      'pointerenter',
      () => {
        for (const thumbnail of nonActiveThumbnails) {
          const srcset = thumbnail.dataset.variationHoverSrcset;
          if (!srcset || !mainImageSizes || isImageLinkQueued('prefetch', srcset)) continue;
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = 'image';
          link.setAttribute('imagesrcset', srcset);
          link.setAttribute('imagesizes', mainImageSizes);
          document.head.appendChild(link);
        }
      },
      { once: true, signal: prefetchController.signal },
    );
  }

  return () => {
    listenerController.abort();
    prefetchController.abort();
  };
}

/**
 * Visually previews a product variation's image and price on a product card when
 * the user hovers or focuses one of the variation thumbnails. On desktop, clicking
 * a thumbnail navigates to that variation's PDP. On touch devices, the first tap
 * selects the variation; a second tap on the selected thumbnail navigates.
 * Lazily initialized via an Intersection Observer.
 * @extends {Component}
 */
export class CardVariationHover extends Component {
  /** @type {HTMLElement | null} */
  #productCard = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #initialized = false;

  /** @type {(() => void) | null} */
  #unbind = null;

  connectedCallback() {
    super.connectedCallback();

    this.#productCard = this.closest('product-card') || this.closest('.resource-card');
    if (!this.#productCard) return;
    this.#observeVisibility();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#unbind?.();
    this.#unbind = null;
  }

  #observeVisibility() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#initialized) {
            this.#initialize();
            this.#observer?.disconnect();
          }
        }
      },
      { rootMargin: '100px' },
    );

    this.#observer.observe(this);
  }

  #initialize() {
    if (!this.#productCard) return;
    this.#initialized = true;
    this.#unbind = bindCardVariationHover(this, this.#productCard);
  }
}

if (!customElements.get('card-variation-hover')) {
  customElements.define('card-variation-hover', CardVariationHover);
}
